/**
 * `/api/auth` — signing in, signing out, and self-service.
 *
 * The session never appears here: it is an httpOnly cookie the browser holds
 * and sends. There is nothing for the app to store, and nothing it could leak.
 */
import type {
  ChangeOwnPasswordRequest,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  MeResponse,
  OkResponse,
  RevokeSessionResponse,
  SessionListResponse,
  UpdateOwnProfileRequest,
  UserResponse,
} from '@forsys/shared';
import { apiFetch } from './client';

/**
 * A wrong password answers 401, exactly as an expired session does — so this
 * opts out of the session-expiry announcement and lets the caller render the
 * failure on the form.
 */
export function login(body: LoginRequest): Promise<LoginResponse> {
  return apiFetch<LoginResponse>('/auth/login', {
    method: 'POST',
    json: body,
    expectUnauthorized: true,
  });
}

export function logout(): Promise<LogoutResponse> {
  return apiFetch<LogoutResponse>('/auth/logout', { method: 'POST' });
}

/**
 * Who the caller is, what they may do, and whether they bypass all of it.
 *
 * 401 here means "nobody is signed in", which is an answer rather than a
 * failure — the app asks this on load precisely to find out.
 */
export function getMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/auth/me', { expectUnauthorized: true });
}

export function updateOwnProfile(body: UpdateOwnProfileRequest): Promise<UserResponse> {
  return apiFetch<UserResponse>('/auth/me', { method: 'PATCH', json: body });
}

/**
 * Changing your own password. Succeeding **ends every session, including this
 * one** — the caller must sign in again, and the UI has to say so.
 */
export function changeOwnPassword(body: ChangeOwnPasswordRequest): Promise<OkResponse> {
  return apiFetch<OkResponse>('/auth/me/password', { method: 'POST', json: body });
}

/**
 * Stop impersonating. The server ends the impersonation and sets a fresh
 * session for the administrator as the cookie; the answer is the administrator.
 */
export function stopImpersonation(): Promise<UserResponse> {
  return apiFetch<UserResponse>('/auth/impersonation', { method: 'DELETE' });
}

/**
 * The caller's own live sessions, most recently used first.
 *
 * `isCurrent` comes from the server and could not come from anywhere else: the
 * session cookie is httpOnly, so the app cannot compare anything itself.
 */
export function listOwnSessions(): Promise<SessionListResponse> {
  return apiFetch<SessionListResponse>('/auth/me/sessions');
}

/**
 * Signing one session out. `wasCurrent` in the answer means it was this one —
 * the cookie is already gone and the app must stop pretending otherwise.
 */
export function revokeOwnSession(id: string): Promise<RevokeSessionResponse> {
  return apiFetch<RevokeSessionResponse>(`/auth/me/sessions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
